/**
 * Board-only values the O01–O07 frames use that are not in the @kk/ui token list (research-screens.md §(c)).
 * This is the ONLY file in apps/portal allowed to hold a hex literal; each entry names its board frame.
 */
export const portalColors = {
  /** O02 "Consent pending" chip fill and the O05 no-code notice (board #FFF4D6; tokens.ts derives tint.sun instead). */
  sunTintFill: '#FFF4D6',
  /** O02 pending-invite row background (board #FFFBEF cream). */
  cream: '#FFFBEF',
  /** O02 "Waiting on parent" amber text (board #D99609). */
  amber: '#D99609',
  /** O02 dashed placeholder disc border on an invite row (board #9AA5B4). */
  dashedDisc: '#9AA5B4',
} as const;

/**
 * **The portal's two small-text colours, darkened for the web portal only** (the 23 Oct audit, T2-11 and Tier 4 #6).
 * `colors.muted` (#6B7A90) is 3.9:1 on the page's #EEF3F7 and `colors.deepTeal` (#0099AB) 3.1:1, under WCAG AA's 4.5:1
 * for the 12 to 13px lines the portal is full of. These pass on the page and on white; the app keeps its own tokens.
 */
export const portalText = {
  /** 5.3:1 on #EEF3F7, 5.9:1 on white. */
  muted: '#56657A',
  /** 4.5:1 on #EEF3F7, 5.1:1 on white. */
  link: '#007A88',
} as const;

const SIDEBAR = 224;
const MAIN_PAD_X = 28;
/** The approvals and roster tables' minimum width: narrower than this, they scroll sideways inside their card. */
const TABLE_MIN = 720;
/** A table card's two 1px borders, and the room a classic (non-overlay) desktop scrollbar takes from the page. */
const TABLE_CARD_BORDERS = 2;
const CLASSIC_SCROLLBAR = 17;

export const portalLayout = {
  /** O01–O05 sidebar width. */
  sidebar: SIDEBAR,
  /** Main column padding (board: 24px 28px). */
  mainPadY: 24,
  mainPadX: MAIN_PAD_X,
  tableMin: TABLE_MIN,
  /**
   * O07 breakpoint: at and under this the shell collapses to the phone layout — header, segments, cards.
   *
   * **It is the narrowest width at which the sidebar and a whole table fit side by side, minus one**, not a device
   * size. It was 600 until 2026-09-17: at 820 (iPad portrait) the fixed 224px sidebar left the approvals table 538 of
   * its 720px, so every row's Approve and Deny began out of view behind a scrollbar iPad Safari does not draw until
   * you are already scrolling. Evan: collapse the sidebar earlier, "so the tablet gets the width instead". Derived
   * rather than typed, so no width can show the sidebar beside a table short of its last column; iPad landscape
   * (1024) keeps the sidebar, and its table fits.
   */
  phoneMax: SIDEBAR + 2 * MAIN_PAD_X + TABLE_MIN + TABLE_CARD_BORDERS + CLASSIC_SCROLLBAR - 1,
  /** O07 side gutter and sticky footer offset. */
  phoneGutter: 20,
  stickyBottom: 44,
  /** Table cell padding (12px 18px) and grid gap. */
  cellPadY: 12,
  cellPadX: 18,
  gridGap: 12,
  /** O01 grid `2fr 2fr 1.2fr 1fr 150px`; O02 grid `2fr 1fr 1.6fr 1.2fr 1.2fr 1fr`. */
  approvalsGrid: '2fr 2fr 1.2fr 1fr 150px',
  rosterGrid: '2fr 1fr 1.6fr 1.2fr 1.2fr 1fr',
  /** O03 assignment list (not drawn; the O02 rhythm) and the O05 staff table (not drawn). */
  assignmentsGrid: '2.4fr 1fr 1fr 1fr',
  staffGrid: '2fr 1fr 1.6fr 1fr',
  /** O05 join code: 30px/800 with .14em tracking on the neutral panel; "JOIN CODE" 11px/.14em. */
  joinCodeTrackingEm: 0.14,
  /** O01 org lockup logo (36, radius 10) and the O07 phone header logo (32). */
  logo: 36,
  logoPhone: 32,
  logoRadius: 10,
  /**
   * The signed-out card's corner (Evan, 2026-09-22: white, 24px radius, the design system's card shadow).
   * Between `radii.card` (20) and `radii.sheet` (28) and named here rather than rounded to one of them,
   * because this is the only card in the product a person meets before they are anybody.
   */
  signInCardRadius: 24,
  /** The turquoise panel's share of a desktop sign-in; the stage takes the rest. */
  signInPanelWidth: '45%',
} as const;
